import React, { useState, useEffect, useMemo } from "react";
import { Search, Trash2 } from "lucide-react";
import { message } from "antd";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "components/ui/dialog";
import CollectionCard from "./CollectionCard";
import CollectionDetailView from "./CollectionDetailView";
import SavedSearchEmptyState from "./SavedSearchEmptyState";
import {
  getSavedSearchResults,
  updateSavedSearchResult,
  deleteSavedSearchResult,
} from "Explore/Services/SavedSearchResults";
import { exportSavedResultsCSV, exportSavedResultsXLSX } from "../../utils/exportSavedResults";

const SavedSearchResultsPanel = ({ activeTab }) => {
  const entityType = useMemo(() => {
    if (activeTab === "saved-units") return "units";
    if (activeTab === "saved-institutions") return "institutions";
    if (activeTab === "saved-venues") return "venues";
    return "researchers";
  }, [activeTab]);

  const title = useMemo(() => {
    if (entityType === "units") return "Saved Unit Searches";
    if (entityType === "institutions") return "Saved Institution Searches";
    if (entityType === "venues") return "Saved Venue Searches";
    return "Saved Researcher Searches";
  }, [entityType]);

  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [selectedCollection, setSelectedCollection] = useState(null);

  const [deleteId, setDeleteId] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameOpen, setRenameOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);

  const loadCollections = async () => {
    setLoading(true);
    try {
      const data = await getSavedSearchResults({ entityType });
      setCollections(data || []);
    } catch (e) {
      console.error("Failed to load saved search collections", e);
      setCollections([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCollections();
    setSearchTerm("");
    setSelectedCollection(null);
  }, [entityType]);

  const filteredCollections = useMemo(() => {
    if (!searchTerm.trim()) return collections;
    const q = searchTerm.toLowerCase();
    return collections.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [collections, searchTerm]);

  const handleOpenDetail = (col) => {
    setSelectedCollection(col);
  };

  const handleExportCSV = (col) => {
    exportSavedResultsCSV(col);
    message.success("CSV export started.");
  };

  const handleExportExcel = async (col) => {
    try {
      await exportSavedResultsXLSX(col);
      message.success("Excel export started.");
    } catch (err) {
      console.error(err);
      message.error("Failed to export Excel file.");
    }
  };

  const promptDelete = (id) => {
    setDeleteId(id);
    setDeleteConfirmOpen(true);
  };

  const promptRename = (col) => {
    setRenameTarget(col);
    setRenameValue(col.name);
    setRenameOpen(true);
  };

  const handleConfirmRename = async () => {
    if (!renameTarget || !renameValue.trim()) return;
    setRenaming(true);
    try {
      const updated = await updateSavedSearchResult(renameTarget.id, { name: renameValue.trim() });
      setCollections((prev) =>
        prev.map((c) => (c.id === renameTarget.id ? { ...c, name: updated.name } : c))
      );
      setSelectedCollection((prev) =>
        prev && prev.id === renameTarget.id ? { ...prev, name: updated.name } : prev
      );
      message.success("Collection renamed successfully.");
      setRenameOpen(false);
      setRenameTarget(null);
    } catch (e) {
      console.error(e);
      let errMsg = "Failed to rename collection.";
      const detail = e?.response?.data?.detail;
      if (Array.isArray(detail) && detail[0]?.msg) errMsg = detail[0].msg;
      else if (typeof detail === "string") errMsg = detail;
      message.error(errMsg);
    } finally {
      setRenaming(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteSavedSearchResult(deleteId);
      setCollections((prev) => prev.filter((c) => c.id !== deleteId));
      if (selectedCollection && selectedCollection.id === deleteId) {
        setSelectedCollection(null);
      }
      message.success("Collection removed.");
    } catch (e) {
      console.error(e);
      message.error("Failed to delete collection.");
    } finally {
      setDeleteConfirmOpen(false);
      setDeleteId(null);
    }
  };

  return (
    <div className="flex flex-col w-full flex-1 pb-6">
      {selectedCollection ? (
        <CollectionDetailView
          collection={selectedCollection}
          onBack={() => setSelectedCollection(null)}
          onRename={() => promptRename(selectedCollection)}
          onDelete={() => promptDelete(selectedCollection.id)}
          onExportCSV={() => handleExportCSV(selectedCollection)}
          onExportExcel={() => handleExportExcel(selectedCollection)}
        />
      ) : (
        <>
          {/* Header & Filter Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-200 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-explore tracking-tight m-0">{title}</h1>
              <p className="text-sm text-gray-500 mt-1">
                Manage saved search snapshots with cohort rankings, metrics, and export data.
              </p>
            </div>

            {collections.length > 0 && (
              <div className="flex items-center gap-3">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search collections..."
                    className="pl-9 h-9 bg-white text-sm focus-visible:ring-explore"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Content */}
          {!loading && collections.length === 0 ? (
            <SavedSearchEmptyState entityType={entityType} />
          ) : !loading && filteredCollections.length === 0 ? (
            <div className="py-16 text-center text-gray-500 bg-white rounded-lg border border-gray-200">
              <p className="text-sm">No collections matching "{searchTerm}"</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredCollections.map((col) => (
                <CollectionCard
                  key={col.id}
                  collection={col}
                  onView={() => handleOpenDetail(col)}
                  onRename={() => promptRename(col)}
                  onExportCSV={() => handleExportCSV(col)}
                  onExportExcel={() => handleExportExcel(col)}
                  onDelete={() => promptDelete(col.id)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="sm:max-w-md bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Trash2 size={18} className="text-rose-600" />
              Delete Collection?
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Are you sure you want to delete this saved search collection? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleConfirmDelete}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-md bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-gray-900">
              Rename Collection
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500">
              Enter a new name for this saved search collection.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Collection name"
              className="h-9 text-sm focus-visible:ring-explore"
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0 mt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRenameOpen(false)}
              disabled={renaming}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleConfirmRename}
              disabled={!renameValue.trim() || renaming}
              className="bg-explore hover:bg-explore/90 text-white"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SavedSearchResultsPanel;
